/**
 * Heating system to compatible energy sources mapping
 * Defines which energy sources are compatible with each heating generator type
 */

import {
  EnergySourceSchema,
  type DHWGeneratorType,
  type EnergySource,
  type HeatingGeneratorType,
} from '../types/isfp.types'

/**
 * Maps heating generator types to their compatible energy sources
 */
export const heatingSystemEnergySources: Record<HeatingGeneratorType, readonly EnergySource[]> = {
  boiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'firewood',
    'pellets',
    'biomass',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  standardBoiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  lowTemperatureBoiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  condensingBoiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  biomassBoiler: ['firewood', 'pellets', 'biomass'],

  heatPump: ['electricity'],

  groundSourceHeatPump: ['electricity'],

  groundWaterWaterHeatPump: ['electricity'],

  airWaterHeatPumpMonovalent: ['electricity'],

  airWaterHeatPumpBivalent: ['electricity'],

  airAirHeatPumpMonovalent: ['electricity'],

  airAirHeatPumpBivalent: ['electricity'],

  electricPanel: ['electricity'],

  stove: ['firewood', 'pellets', 'biomass', 'mineralCoal', 'brownCoal', 'blackCoal', 'coke'],

  exhaustWaterHeatPump: ['electricity'],

  exhaustAirHeatPump: ['electricity'],

  solarThermalSystem: ['solar'],

  // Heating generator types with all energy sources available
  districtHeating: EnergySourceSchema.options,
  vrf: EnergySourceSchema.options,
  chp: EnergySourceSchema.options,
  fuelCell: EnergySourceSchema.options,
}

/**
 * Get compatible energy sources for a given heating generator type
 * @param heatingGeneratorType - The heating generator type
 * @returns Array of compatible energy sources
 */
export const getCompatibleEnergySources = (
  heatingGeneratorType: HeatingGeneratorType
): readonly EnergySource[] => {
  return heatingSystemEnergySources[heatingGeneratorType] || []
}

/**
 * Check if an energy source is compatible with a heating generator type
 * @param heatingGeneratorType - The heating generator type
 * @param energySource - The energy source to check
 * @returns True if the energy source is compatible, false otherwise
 */
export const isEnergySourceCompatible = (
  heatingGeneratorType: HeatingGeneratorType,
  energySource: EnergySource
): boolean => {
  const compatibleSources = heatingSystemEnergySources[heatingGeneratorType]
  return compatibleSources.includes(energySource)
}

/**
 * Maps DHW generator types to their compatible energy sources
 */
export const dhwSystemEnergySources: Record<DHWGeneratorType, readonly EnergySource[]> = {
  combinedWithHeating: EnergySourceSchema.options,

  boiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'firewood',
    'pellets',
    'biomass',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  standardBoiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  lowTemperatureBoiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  condensingBoiler: [
    'heatingOil',
    'bioOil',
    'gas',
    'bioGas',
    'liquefiedGas',
    'mineralCoal',
    'brownCoal',
    'blackCoal',
    'coke',
  ],

  biomassBoiler: ['firewood', 'pellets', 'biomass'],

  heatPump: ['electricity'],

  groundSourceHeatPump: ['electricity'],

  groundWaterWaterHeatPump: ['electricity'],

  airWaterHeatPumpMonovalent: ['electricity'],

  airWaterHeatPumpBivalent: ['electricity'],

  exhaustAirAirWaterHeatPump: ['electricity'],

  electricFlowHeater: ['electricity'],

  gasFlowHeater: ['gas', 'bioGas', 'liquefiedGas'],

  solarThermalSystem: ['solar'],

  // DHW generator types with all energy sources available
  districtHeating: EnergySourceSchema.options,
  chp: EnergySourceSchema.options,
  dhwStation: EnergySourceSchema.options,
}

/**
 * Get compatible energy sources for a given DHW generator type
 * @param dhwGeneratorType - The DHW generator type
 * @returns Array of compatible energy sources
 */
export const getCompatibleDHWEnergySources = (
  dhwGeneratorType: DHWGeneratorType
): readonly EnergySource[] => {
  return dhwSystemEnergySources[dhwGeneratorType] || []
}

/**
 * Check if an energy source is compatible with a DHW generator type
 * @param dhwGeneratorType - The DHW generator type
 * @param energySource - The energy source to check
 * @returns True if the energy source is compatible, false otherwise
 */
export const isDHWEnergySourceCompatible = (
  dhwGeneratorType: DHWGeneratorType,
  energySource: EnergySource
): boolean => {
  const compatibleSources = dhwSystemEnergySources[dhwGeneratorType]
  return compatibleSources.includes(energySource)
}

/**
 * Vice-versa mapping: Energy sources to compatible heating generator types
 * Automatically generated from heatingSystemEnergySources
 */
export const energySourceToHeatingGenerators: Record<
  EnergySource,
  readonly HeatingGeneratorType[]
> = (() => {
  const result: Record<string, HeatingGeneratorType[]> = {}

  // Initialize all energy sources with empty arrays
  EnergySourceSchema.options.forEach(energySource => {
    result[energySource] = []
  })

  // Populate the reverse mapping
  Object.entries(heatingSystemEnergySources).forEach(([generatorType, energySources]) => {
    energySources.forEach(energySource => {
      if (!result[energySource].includes(generatorType as HeatingGeneratorType)) {
        result[energySource].push(generatorType as HeatingGeneratorType)
      }
    })
  })

  return result as Record<EnergySource, readonly HeatingGeneratorType[]>
})()

/**
 * Get compatible heating generator types for a given energy source
 * @param energySource - The energy source
 * @returns Array of compatible heating generator types
 */
export const getCompatibleHeatingGenerators = (
  energySource: EnergySource
): readonly HeatingGeneratorType[] => {
  return energySourceToHeatingGenerators[energySource] || []
}

/**
 * Vice-versa mapping: Energy sources to compatible DHW generator types
 * Automatically generated from dhwSystemEnergySources
 */
export const energySourceToDHWGenerators: Record<EnergySource, readonly DHWGeneratorType[]> =
  (() => {
    const result: Record<string, DHWGeneratorType[]> = {}

    // Initialize all energy sources with empty arrays
    EnergySourceSchema.options.forEach(energySource => {
      result[energySource] = []
    })

    // Populate the reverse mapping
    Object.entries(dhwSystemEnergySources).forEach(([generatorType, energySources]) => {
      energySources.forEach(energySource => {
        if (!result[energySource].includes(generatorType as DHWGeneratorType)) {
          result[energySource].push(generatorType as DHWGeneratorType)
        }
      })
    })

    return result as Record<EnergySource, readonly DHWGeneratorType[]>
  })()

/**
 * Get compatible DHW generator types for a given energy source
 * @param energySource - The energy source
 * @returns Array of compatible DHW generator types
 */
export const getCompatibleDHWGenerators = (
  energySource: EnergySource
): readonly DHWGeneratorType[] => {
  return energySourceToDHWGenerators[energySource] || []
}
